import {
  IMPORT_USER_URL,
  IMPORT_VENDOR_URL,
  USER_CSV_TEMPLATE_URL,
  USER_PROFILE_CSV_TEMPLATE_ENDPOINT,
  VENDOR_CSV_TEMPLATE_URL,
} from '@webapp/api/ImportAPI'
import { ImportTypes } from '@webapp/models/enums'
import type { IImporterProps } from '@webapp/platform/_unmigrated/Importer/Importer'

/**
 * This file is a temporary holding place for importer options constants.
 * TODO: Reevaluate where these should live
 */

export const USER_IMPORTER_OPTIONS: Omit<IImporterProps, 'title'> = {
  importEndpoint: IMPORT_USER_URL,
  templateEndpoint: USER_CSV_TEMPLATE_URL,
  requiredFields: [
    'first_name',
    'last_name',
    'email',
    'send_user_invitation_email',
  ],
  optionalFields: [
    'is_sso_enabled',
    'position',
    'phone',
    'action_on_duplicate',
    'location',
    'department',
  ],
  fieldNameMapper: {
    first_name: 'First Name',
    last_name: 'Last Name',
    location: 'Home Location',
    department: 'Home Department',
    is_sso_enabled: 'Enable SSO',
    email: 'Email',
    phone: 'Phone Number',
    position: 'Company Position',
    action_on_duplicate: 'Action On Duplicate',
    send_user_invitation_email: 'Send User Invitation Email',
  },
}
export const USER_IMPORTER_TYPE = ImportTypes.USER

export const ENTITY_FIELD = 'entity'

/**
 * When Multi-Entity is enabled, the user import gains a required Entity column
 * and switches to the backend-rendered (gated) template. Otherwise the base
 * options are returned unchanged.
 */
export const getUserImporterOptions = (
  isMultiEntityEnabled: boolean
): Omit<IImporterProps, 'title'> => {
  if (!isMultiEntityEnabled) {
    return USER_IMPORTER_OPTIONS
  }

  return {
    ...USER_IMPORTER_OPTIONS,
    templateEndpoint: USER_PROFILE_CSV_TEMPLATE_ENDPOINT,
    requiredFields: [...USER_IMPORTER_OPTIONS.requiredFields, ENTITY_FIELD],
    fieldNameMapper: {
      ...USER_IMPORTER_OPTIONS.fieldNameMapper,
      [ENTITY_FIELD]: 'Entity',
    },
  }
}

export const VENDOR_IMPORTER_OPTIONS = {
  importEndpoint: IMPORT_VENDOR_URL,
  templateEndpoint: VENDOR_CSV_TEMPLATE_URL,
  requiredFields: ['name'],
  optionalFields: [
    'address_1',
    'postal_code',
    'city',
    'state_province',
    'country',
    'phone_1',
    'address_2',
    'phone_2',
    'fax',
    'email',
    'comment',
    'contact',
    'url',
    'external_id',
    'payment_terms',
    'shipping_terms',
    'shipping_method',
    'action_on_duplicate',
    'preferred',
    'payment_method_name',
    'payment_method_type',
    'payment_method_currency',
    'payable_to',
    'routing_number',
    'account_number',
    'company_name',
    'description',
    'tax',
    'is_1099_eligible',
    'payment_method_eft_institution_number',
    'payment_method_eft_transit_number',
    'payment_method_bank_account_holder_name',
    'payment_method_bank_name',
  ],
  fieldNameMapper: {
    name: 'Name',
    city: 'City',
    country: 'Country',
    address_1: 'Address One',
    address_2: 'Address Two',
    phone_1: 'Phone One',
    phone_2: 'Phone Two',
    email: 'Email',
    url: 'Website',
    fax: 'Fax',
    external_id: 'External ID',
    payment_terms: 'Payment Terms',
    shipping_terms: 'Shipping Terms',
    shipping_method: 'Shipping Method',
    postal_code: 'Zip / Postal Code',
    state_province: 'State / Province',
    shipping_method_ref: 'Shipping Method',
    payment_terms_ref: 'Payment Terms',
    shipping_terms_ref: 'Shipping Terms',
    action_on_duplicate: 'Action On Duplicate',
    preferred: 'Preferred',
    payment_method_name: 'Payment Method Name',
    payment_method_type: 'Payment Method Type',
    payment_method_currency: 'Payment Method Currency',
    payable_to: 'Payable To',
    routing_number: 'ACH Routing Number',
    account_number: 'ACH Account Number',
    company_name: 'ACH Company Name',
    description: 'Payment Method Description',
    tax: 'Tax',
    is_1099_eligible: 'Is the vendor 1099 eligible?',
    payment_method_eft_institution_number:
      'Payment Method EFT Institution Number',
    payment_method_eft_transit_number: 'Payment Method EFT Transit Number',
    payment_method_bank_account_holder_name:
      'Payment Method Bank Account Holder Name',
    payment_method_bank_name: 'Payment Method Bank Name',
  },
  fieldMatcher: {
    address_1: ['Address 1', 'address'],
    address_2: ['Address 2'],
    phone_1: ['Phone 1', 'phone'],
    phone_2: ['Phone 2'],
    postal_code: ['ZIP/POSTAL CODE', 'postal'],
    state_province: ['STATE/PROVINCE', 'state', 'province'],
    action_on_duplicate: ['ACTION ON DUPLICATE'],

    preferred: ['Preferred', 'PREFERRED'],

    payment_method_name: ['Payment Method Name', 'PAYMENT METHOD NAME'],
    payment_method_type: ['Payment Method Type', 'PAYMENT METHOD TYPE'],
    payment_method_currency: [
      'Payment Method Currency',
      'PAYMENT METHOD CURRENCY',
    ],
    payable_to: ['Payable To', 'PAYABLE TO'],
    routing_number: ['Routing Number', 'ROUTING NUMBER'],
    account_number: ['Account Number', 'ACCOUNT NUMBER'],
    company_name: ['Company Name', 'COMPANY NAME', 'Payee Name', 'PAYEE NAME'],
    description: ['Description', 'DESCRIPTION'],
    tax: ['Tax', 'TAX'],
    is_1099_eligible: ['1099 Eligible', '1099 ELIGIBLE'],
    payment_method_eft_institution_number: [
      'Payment Method EFT Institution Number',
      'PAYMENT METHOD EFT INSTITUTION NUMBER',
    ],
    payment_method_eft_transit_number: [
      'Payment Method EFT Transit Number',
      'PAYMENT METHOD EFT TRANSIT NUMBER',
    ],
    payment_method_bank_account_holder_name: [
      'Payment Method Bank Account Holder Name',
      'PAYMENT METHOD BANK ACCOUNT HOLDER NAME',
    ],
    payment_method_bank_name: [
      'Payment Method Bank Name',
      'PAYMENT METHOD BANK NAME',
    ],
  },
}
export const VENDOR_IMPORTER_TYPE = ImportTypes.VENDOR
